import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AppendixContext = createContext();

const STORAGE_KEY = 'cti_appendix_layout';

export const useAppendix = () => {
    const context = useContext(AppendixContext);
    if (!context) {
        throw new Error('useAppendix must be used within an AppendixProvider');
    }
    return context;
};

export const AppendixProvider = ({ children }) => {
    // State for each appendix instance
    const [appendices, setAppendices] = useState({});
    const [leadSearchRequest, setLeadSearchRequest] = useState(null);
    const [layout, setLayout] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error('Error loading appendix layout:', e);
            return {};
        }
    });
    
    // Stack de interacción para controlar z-index
    // El último elemento del array es el más reciente (z-index más alto)
    const [interactionStack, setInteractionStack] = useState([]);

    // Save layout whenever it changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    }, [layout]);

    // Trae un apéndice al frente del stack de interacción
    const bringToFront = useCallback((id) => {
        setInteractionStack(prev => {
            // Remover el id si ya existe
            const filtered = prev.filter(item => item !== id);
            // Agregarlo al final (más reciente)
            return [...filtered, id];
        });
    }, []);

    // Calcula el z-index dinámico basado en la posición en el stack
    const getZIndex = useCallback((id, isDragging = false) => {
        const BASE_Z_INDEX = 100;
        const position = interactionStack.indexOf(id);
        
        if (isDragging) {
            // El que se está arrastrando siempre arriba de todo
            return BASE_Z_INDEX + interactionStack.length + 100;
        }
        
        if (position === -1) {
            // No está en el stack, asignar índice base
            return BASE_Z_INDEX;
        }
        
        // Calcular z-index basado en posición en el stack
        return BASE_Z_INDEX + position;
    }, [interactionStack]);

    const registerAppendix = useCallback((id, data) => {
        setAppendices(prev => {
            // If already registered, merge the new data but preserve position
            if (prev[id]) {
                return {
                    ...prev,
                    [id]: { 
                        ...prev[id], 
                        ...data,
                        id,
                        position: prev[id].position || layout[id] || data.defaultPosition || null
                    }
                };
            }
            // First time registration
            return {
                ...prev,
                [id]: {
                    id,
                    isOpen: false,
                    isMinimized: false,
                    isMaximized: false,
                    ...data,
                    position: layout[id] || data.defaultPosition || null
                }
            };
        });
    }, [layout]);

    const updatePosition = useCallback((id, position) => {
        setLayout(prev => ({
            ...prev,
            [id]: position
        }));
        setAppendices(prev => {
            if (!prev[id]) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], position }
            };
        });
    }, []);

    const openAppendix = useCallback((id) => {
        setAppendices(prev => {
            if (!prev[id]) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], isOpen: true, isMinimized: false }
            };
        });
        // Traer al frente cuando se abre
        bringToFront(id);
    }, [bringToFront]);

    const searchLeadByPhone = useCallback((phone) => {
        setLeadSearchRequest({ phone, requestId: Date.now() });
        openAppendix('lead-deep-search');
    }, [openAppendix]);

    const closeAppendix = useCallback((id) => {
        setAppendices(prev => {
            if (!prev[id]) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], isOpen: false, isMinimized: false }
            };
        });
        // Remover del stack de interacción al cerrar
        setInteractionStack(prev => prev.filter(item => item !== id));
    }, []);

    const toggleMinimize = useCallback((id) => {
        setAppendices(prev => {
            if (!prev[id]) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], isMinimized: !prev[id].isMinimized }
            };
        });
    }, []);

    const toggleMaximize = useCallback((id) => {
        setAppendices(prev => {
            if (!prev[id]) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], isMaximized: !prev[id].isMaximized }
            };
        });
    }, []);

    const setAppendixStatus = useCallback((id, isOpen) => {
        setAppendices(prev => {
            // If appendix doesn't exist yet, register it first with defaults
            if (!prev[id]) {
                return {
                    ...prev,
                    [id]: {
                        id,
                        title: id,
                        icon: null,
                        isOpen,
                        isMinimized: false,
                        isMaximized: false,
                        position: layout[id] || null
                    }
                };
            }
            // Update existing appendix
            return {
                ...prev,
                [id]: { ...prev[id], isOpen, isMinimized: false }
            };
        });
    }, [layout]);

    const value = {
        appendices,
        registerAppendix,
        openAppendix,
        closeAppendix,
        toggleMinimize,
        toggleMaximize,
        setAppendixStatus,
        updatePosition,
        bringToFront,
        getZIndex,
        interactionStack,
        leadSearchRequest,
        searchLeadByPhone
    };

    return (
        <AppendixContext.Provider value={value}>
            {children}
        </AppendixContext.Provider>
    );
};
